// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@flip/App/Components/Common/XngZone";
import Notify from "@flip/App/Components/Notify";
import { XngAddAccountParameter } from "@flip/App/Handlers/CreateAccount/AddAccount";
import loadSystemList from "@flip/App/Handlers/SystemList/SystemListHandler";
import {
    AccountFormState,
    initialAccountFormState,
} from "@flip/Overrides/Widgets/CreateAccount/CreateAccount.form.module";
import CreateAccountLongForm from "@flip/Overrides/Widgets/CreateAccount/CreateAccount.LongForm";
import CreateAccountShortForm from "@flip/Overrides/Widgets/CreateAccount/CreateAccount.ShortForm";
import { color, doScroll, xScreen } from "@flip/XDL/Utilities";
import { CreateForm, FormField } from "@flip/XDL/Utilities/form";
import { xValidateOnSubmit } from "@flip/XDL/Utilities/xValidate";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/AddAccount";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import translate from "@insite/client-framework/Translate";
import { CountryModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxStyle } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupPresentationProps } from "@insite/mobius/CheckboxGroup";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Radio from "@insite/mobius/Radio";
import RadioGroup from "@insite/mobius/RadioGroup";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import cloneDeep from "lodash/cloneDeep";
import React, { RefObject, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
// !!! XNG-CHANGES !!!
// This is fully customized widget no need to upgrade

const mapDispatchToProps = {
    addAccount,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
    loadCurrentCountries,
    loadSystemList,
};

const mapStateToProps = (state: ApplicationState) => ({
    accountSettings: getSettingsCollection(state).accountSettings,
    countries: getCurrentCountries(state),
});

type Props = WidgetProps &
    HasShellContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CreateAccountStyles {
    form?: InjectableCss;
    centeringWrapper?: InjectableCss;
    titleBorder?: GridItemProps;
    mainGridContainer?: GridContainerProps;
    userGridItem?: GridItemProps;
    lineGridItem?: GridItemProps;
    userGridContainer?: GridContainerProps;
    subscriptionGridItem?: GridItemProps;
    submitErrorGridItem?: GridItemProps;
    submitErrorTitle?: TypographyProps;
    submitGridItem?: GridItemProps;
    reCaptchaGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    fieldGridItem?: GridItemProps;
    projectsCheckboxGroup?: CheckboxGroupPresentationProps;
    notifyError?: TypographyPresentationProps;
    createAccountSubTitle?: TypographyProps;
    flipDocumentGridItem?: GridItemProps;
    // XNG-Changes: FLIP-1177
    regitrationTaxGridItem?: GridItemProps;
    regitrationTaxCheckbox?: CheckboxPresentationProps;
}

export const createAccountStyles: CreateAccountStyles = {
    userGridItem: {
        width: 12,
    },
    lineGridItem: {
        width: 12,
        css: css`
            align-items: baseline;
            flex-wrap: wrap;

            p {
                margin-top: 24px;
            }

            &:after {
                content: "";
                display: block;
                width: 100%;
                height: 3px;
                background-color: ${color("common.border")};
                margin-bottom: 16px;
            }
        `,
    },
    subscriptionGridItem: {
        width: 12,
    },
    submitErrorGridItem: {
        width: 12,
    },
    submitGridItem: {
        width: 12,
    },
    fieldGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    projectsCheckboxGroup: {
        css: css`
            flex-direction: row;
            flex-wrap: wrap;
            legend {
                width: 100%;
            }
            ${CheckboxStyle} {
                flex: 1 1 50%;
                margin-top: 10px;

                ${xScreen("lt", "sm")} {
                    flex: 1 1 100%;
                }
            }
        `,
    },
    notifyError: {
        css: css`
            margin: 0;
            width: 100%;
        `,
    },
    createAccountSubTitle: {
        variant: "p",
        css: css`
            width: fit-content;
            font-weight: bold;
            letter-spacing: 0;
            line-height: 24px;
        `,
    },
    flipDocumentGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
};

const styles = createAccountStyles;
const StyledForm = getStyledWrapper("form");

const formId = "createAccountForm";
export const stFormId = "createAccountSTFrom";
export const btFormId = "createAccountBTFrom";
export const mcFormId = "createAccountMainContactFrom";

const CreateAccount = (props: Props) => {
    const [errorMessage, setErrorMessage] = useState("");
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [isTaxRegistered, setIsTaxRegistered] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [accountFormState, setAccountFormState] = useState<AccountFormState>(cloneDeep(initialAccountFormState));
    const [accountCreated, setAccountCreated] = useState(false);
    const isShellEditingMood = props.shellContext?.isEditing;
    const [country, setCountry] = useState<CountryModel[]>();
    const [marketSegmentList, setMarketSegmentList] = useState<any>();
    const [businessTypeList, setBusinessTypeList] = useState<any>();
    const [addressesErrors, setAddressesErrors] = useState<any>({});
    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();

    useEffect(() => {
        props.loadCurrentCountries();
        props.loadSystemList({
            name: "MarketSegment",
            onComplete(result) {
                setMarketSegmentList(result.apiResult?.values);
            },
        });
        props.loadSystemList({
            name: "BusinessType",
            onComplete(result) {
                setBusinessTypeList(result.apiResult?.values);
            },
        });
    }, []);

    useEffect(() => {
        if (props.countries?.length) {
            setCountry(props.countries);
        }
    }, [props.countries]);

    const accountForm = new CreateForm<AccountFormState>(accountFormState, setAccountFormState, {
        accountSettings: props.accountSettings,
    });
    const { fields, isSubmitted } = accountForm.state;

    const onSuccessHandler = () => {
        setErrorMessage("");
        setIsSubmitting(false);
        setAccountFormState(initialAccountFormState);
        setAccountCreated(true);
        doScroll(true);
    };

    const buildFormParamsFromState = () => {
        const formData = {} as any;
        const { fields } = accountForm.state;
        for (const field in fields) {
            const data: FormField<string | boolean> = fields[field as keyof typeof fields];
            if (data.value && !data.error) {
                formData[field] = data.value;
            }
        }

        return formData;
    };

    const validateAddressCountryState = () => {
        const addressesErrorsObj = {} as any;
        if (!fields.sCountry.value) {
            addressesErrorsObj.sCountry = siteMessage("Field_Required", "Country");
        }

        if (!fields.sState.value) {
            addressesErrorsObj.sState = siteMessage("Field_Required", "State");
        }

        if (!fields.bCountry.value) {
            addressesErrorsObj.bCountry = siteMessage("Field_Required", "Country");
        }

        if (!fields.bState.value) {
            addressesErrorsObj.bState = siteMessage("Field_Required", "State");
        }
        setAddressesErrors(addressesErrorsObj);
    };

    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (isSubmitting) {
            return;
        }
        setAddressesErrors({});
        // if (!isSubmitted) {
        //     accountForm.setSubmitted();
        // }

        // if (!accountForm.validate()) {
        //     return;
        // }
        let bStateId = "";
        let sStateId = "";

        const { fields } = accountForm.state;
        const errors = xValidateOnSubmit(formId);
        const stErrors = xValidateOnSubmit(stFormId);
        const btErrors = xValidateOnSubmit(btFormId);
        const mcErrors = xValidateOnSubmit(mcFormId);
        validateAddressCountryState();

        if (
            errors.length > 0 ||
            stErrors.length > 0 ||
            btErrors.length > 0 ||
            mcErrors.length > 0 ||
            Object.keys(addressesErrors).length > 0
        ) {
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({ location: "CreateAccount", recaptchaRef });
        if (!isReCaptchaValid) {
            return;
        }
        let formData = buildFormParamsFromState();

        if (!formData) {
            return;
        }
        // XNG-Changes:
        // extract id from selected states and countries
        const bCountryDetails = props.countries?.find(country => country.name === fields.bCountry.value);
        const sCountryDetails = props.countries?.find(country => country.name === fields.sCountry.value);
        sStateId = sCountryDetails?.states?.find(state => state.name === fields.sState.value)?.id || "";
        bStateId = bCountryDetails?.states?.find(state => state.name === fields.bState.value)?.id || "";
        setIsSubmitting(true);
        formData = {
            ...formData,
            newCustomer: formData.isNewCustomer === "true" ? "false" : "true",
            bCountry: bCountryDetails?.id,
            sCountry: sCountryDetails?.id,
            // when no states are found then send an empty string in the request
            sState: sStateId,
            bState: bStateId,
            bStateName: fields.bState.value,
        };
        delete formData.isNewCustomer;

        props.addAccount({
            ...formData,
            onSuccess: onSuccessHandler,
            onError: (error: string) => {
                setErrorMessage(error);
                setIsSubmitting(false);
            },
            onComplete(resultProps: any) {
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        } as XngAddAccountParameter);
    };

    const isFormValid = accountForm.validate();

    if (accountCreated) {
        return (
            <XngZone
                hint="This Zone is for AccountCreated Confirmation"
                zoneName="AccountCreated"
                contentId={props.id}
            />
        );
    }

    return (
        <StyledForm {...styles.form} onSubmit={submitHandler} noValidate>
            <GridContainer {...styles.mainGridContainer}>
                <GridItem {...styles.userGridItem}>
                    <RadioGroup
                        name="isNewCustomer"
                        label={siteMessage("Flip_Create_Account_Form_Type")}
                        value={fields.isNewCustomer.value}
                        onChangeHandler={e => {
                            accountForm?.onFieldChange(fields.isNewCustomer, e.currentTarget.value);
                        }}
                        error={isSubmitted && fields.isNewCustomer.error}
                        {...fields.isNewCustomer.attrs}
                        horizontal={true}
                    >
                        <Radio value="true">{translate("Yes")}</Radio>
                        <Radio value="false">{translate("No")}</Radio>
                    </RadioGroup>
                </GridItem>
                <GridItem {...styles.lineGridItem}>
                    <Typography {...styles.createAccountSubTitle}>{translate("Account Information")}</Typography>
                </GridItem>
                <GridItem width={12}>
                    <GridContainer gap={20}>
                        {/** XNG-Changes: FLIP-992 */}
                        <GridItem width={12} style={{ justifyContent: "flex-end" }}>
                            <Typography variant="legend">{translate("*Required")}</Typography>
                        </GridItem>
                        <CreateAccountShortForm accountForm={accountForm} formId={formId} />
                        <CreateAccountLongForm
                            countries={country}
                            businessTypeList={businessTypeList}
                            marketSegmentList={marketSegmentList}
                            accountForm={accountForm}
                            id={props.id}
                            formId={formId}
                            addressesErrors={addressesErrors}
                            setAddressesErrors={setAddressesErrors}
                        />
                    </GridContainer>
                </GridItem>
                {/* XNG-Changes: FLIP-1177 */}
                <GridItem {...styles.regitrationTaxGridItem}>
                    <CheckboxGroup>
                        <Checkbox
                            {...styles.regitrationTaxCheckbox}
                            onChange={(e, value) => setIsTaxRegistered(value)}
                            checked={isTaxRegistered}
                            data-test-selector="createAccount_Registration_Tax"
                        >
                            {siteMessage("Custom_Registration_Tax")}
                        </Checkbox>
                    </CheckboxGroup>
                </GridItem>
                <GridItem {...styles.subscriptionGridItem}>
                    <CheckboxGroup>
                        <Checkbox
                            onChange={(e, value) => setIsSubscribed(value)}
                            checked={isSubscribed}
                            data-test-selector="createAccount_Subscription"
                        >
                            {siteMessage("Flip_Create_Account_Accuracy_Completeness_Checkbox")}
                        </Checkbox>
                    </CheckboxGroup>
                </GridItem>
                <GridItem {...styles.flipDocumentGridItem}>
                    <Typography variant="p">
                        {siteMessage("Flip_International_Document_Notification_Message")}
                    </Typography>
                    <Button
                        variant="secondary"
                        sizeVariant="medium"
                        onClick={() => {
                            window.open(siteMessage("Flip_International_Document_Link") as string, "blank");
                        }}
                    >
                        {translate("Download Document")}
                    </Button>
                </GridItem>
                {errorMessage && (
                    <GridItem data-test-selector="createAccount_ErrorMessage" {...styles.submitErrorGridItem}>
                        <Notify type="danger" {...styles.notifyError}>
                            <Typography {...styles.submitErrorTitle}>{errorMessage}</Typography>
                        </Notify>
                    </GridItem>
                )}
                <GridItem {...styles.reCaptchaGridItem}>
                    <ReCaptcha recaptchaRef={recaptchaRef} location="CreateAccount" />
                </GridItem>
                <GridItem {...styles.submitGridItem}>
                    <Button
                        {...styles.submitButton}
                        type="submit"
                        disabled={isSubmitting || !isSubscribed || !isTaxRegistered}
                        data-test-selector="createAccount_createButton"
                    >
                        {isSubmitting ? translate("Creating ...") : translate("Create My Account")}
                    </Button>
                </GridItem>
            </GridContainer>
            {isShellEditingMood && (
                <XngZone
                    hint="This Zone is for Account Created Confirmation Content"
                    zoneName="AccountCreated"
                    contentId={props.id}
                />
            )}
        </StyledForm>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(CreateAccount)),
    definition: {
        allowedContexts: ["CreateAccountPage"],
        group: "Create Account",
    },
};

export default widgetModule;
